import React from 'react';
import { Form } from '@heroui/form';
import { Button, Input } from '@heroui/react';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MeasurementConfig, MeasurementState, GraphType } from './CorrectedAgeManager/types';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

interface AdditionalMeasurementsProps {
  config: MeasurementConfig;
  graphType: GraphType;
  measurementState: MeasurementState;
  setMeasurementState: (state: MeasurementState) => void;
  birthDate: Date | null;
  calculateWeek: (date: Date) => number;
}

export const AdditionalMeasurements: React.FC<AdditionalMeasurementsProps> = ({
  config,
  graphType,
  measurementState,
  setMeasurementState,
  birthDate,
  calculateWeek,
}) => {
  const ranges = graphType === 'WHO' ? config.ranges.WHO : config.ranges.FENTON;

  const addMeasurement = () => {
    // Use an "invalid" Date to keep types intact but show empty input in DatePicker
    const emptyDate = new Date(NaN);

    setMeasurementState({
      ...measurementState,
      additionalEntries: [
        ...measurementState.additionalEntries,
        { date: emptyDate, week: '' as unknown as number, value: '' as unknown as number },
      ],
    });
  };

  const removeMeasurement = (index: number) => {
    setMeasurementState({
      ...measurementState,
      additionalEntries: measurementState.additionalEntries.filter((_, i) => i !== index),
    });
  };

  const updateMeasurement = (index: number, field: 'date' | 'value', value: unknown) => {
    const newEntries = [...measurementState.additionalEntries];

    if (field === 'date' && value instanceof Date) {
      const week = calculateWeek(value);
      newEntries[index] = {
        ...newEntries[index],
        date: value,
        week,
      };
    } else if (field === 'value') {
      newEntries[index] = {
        ...newEntries[index],
        value: value as number,
      };
    }

    setMeasurementState({
      ...measurementState,
      additionalEntries: newEntries,
    });
  };

  return (
    <Form className="mb-3">
      <h3>הוספת מדידות</h3>
      <h4>מדידה בלידה</h4>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
        <div style={{ width: '295px', height: '48px' }}>
          <Input
            type="number"
            inputMode="decimal"
            placeholder={`${config.label} ב${config.unit}`}
            min={ranges.min}
            max={ranges.max}
            step={0.01}
            value={measurementState.birthValue}
            onFocus={(e) => e.target.select()}
            onChange={(e) =>
              setMeasurementState({
                ...measurementState,
                birthValue: e.target.value,
              })
            }
            variant="bordered"
            classNames={{
              inputWrapper: `
                bg-white border rounded-sm
                hover:border-blue-600 focus-within:border-blue-700
                border-blue-500
                h-10 min-h-10
              `,
              input: 'bg-white h-full',
            }}
          />
        </div>
      </div>

      <h4>מדידות נוספות</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {measurementState.additionalEntries.map((entry, index) => {
          const selectedDate =
            entry.date instanceof Date && !isNaN(entry.date.getTime()) ? entry.date : null;

          return (
            <div key={index} style={{ display: 'flex', gap: '1rem'}}>
              <div style={{ width: '130px' }}>
                <DatePicker
                  selected={selectedDate}
                  onChange={(date: Date | null) => date && updateMeasurement(index, 'date', date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="תאריך"
                  className="h-10 min-h-10 w-full bg-white border border-blue-500 rounded-sm px-3"
                  maxDate={new Date()}
                  popperProps={{ strategy: 'fixed' }}
                />
              </div>

              <div style={{ width: '150px' }}>
                <Input
                  type="number"
                  inputMode="decimal"
                  min={ranges.min}
                  max={ranges.max}
                  step={0.01}
                  value={(entry as any).value === '' ? '' : String((entry as any).value)}
                  placeholder={`${config.label} ב${config.unit}`}
                  onChange={(e) =>
                    updateMeasurement(
                      index,
                      'value',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                  variant="bordered"
                  classNames={{
                    inputWrapper: `
                      bg-white border rounded-sm
                      hover:border-blue-600 focus-within:border-blue-700
                      border-blue-500
                      h-10 min-h-10
                    `,
                    input: 'bg-white h-full',
                  }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FontAwesomeIcon
                  icon={faTrash}
                  style={{ cursor: 'pointer' }}
                  onClick={() => removeMeasurement(index)}
                />
              </div>
            </div>
          );
        })}
        <Button
          onClick={addMeasurement}
          style={{
            background: 'transparent',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '20px',
            alignSelf: 'flex-start',
            padding: 0,
          }}
        >
          <span>+</span>
          <span>הוספת מדידה מתאריך אחר</span>
        </Button>
      </div>
    </Form>
  );
};